import React from "react";
import { useLocation } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { getLogger } from "@/utils/logger";
import { isStaleChunkError, reloadForStaleChunk } from "@/utils/staleChunkReload";

const log = getLogger("RouteErrorBoundary");

class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      isStaleChunk: false,
      isReloading: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isStaleChunk: isStaleChunkError(error) };
  }

  componentDidCatch(error, errorInfo) {
    log.error(
      "[RouteErrorBoundary:componentDidCatch] > [Error]: uncaught route error",
      error,
      {
        routeName: this.props.routeName || "unknown",
        pathname: this.props.pathname || "unknown",
        componentStack: errorInfo?.componentStack || "",
      }
    );
    if (isStaleChunkError(error)) {
      const reloading = reloadForStaleChunk(
        `RouteErrorBoundary:${this.props.routeName || "unknown"}`
      );
      if (reloading) {
        this.setState({ isReloading: true });
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pathname !== this.props.pathname && this.state.hasError) {
      this.setState({ hasError: false, isStaleChunk: false });
    }
  }

  handleRetry = () => {
    if (this.state.isStaleChunk) {
      // A boundary reset would re-import the same dead chunk URL from the old
      // build; only a full reload picks up the latest deploy.
      log.info("[RouteErrorBoundary:handleRetry] > [Request]: full reload for stale chunk", {
        routeName: this.props.routeName || "unknown",
        pathname: this.props.pathname || "unknown",
      });
      window.location.reload();
      return;
    }
    log.info("[RouteErrorBoundary:handleRetry] > [Request]: reset route boundary", {
      routeName: this.props.routeName || "unknown",
      pathname: this.props.pathname || "unknown",
    });
    this.setState((previousState) => ({
      hasError: false,
      retryCount: previousState.retryCount + 1,
    }));
    log.info("[RouteErrorBoundary:handleRetry] > [Response]: route boundary reset complete");
  };

  render() {
    if (!this.state.hasError) {
      return <React.Fragment key={this.state.retryCount}>{this.props.children}</React.Fragment>;
    }

    if (this.state.isReloading) {
      return (
        <div className="w-full rounded-lg border border-[#324c48]/20 bg-white p-4">
          <Alert className="border-[#324c48]/20">
            <RefreshCw className="h-4 w-4 animate-spin text-[#324c48]" />
            <AlertTitle className="text-[#324c48]">Updating to the latest version</AlertTitle>
            <AlertDescription className="text-sm text-gray-600">
              A new version of the site was deployed. Reloading the page…
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return (
      <div className="w-full rounded-lg border border-[#324c48]/20 bg-white p-4">
        <Alert className="border-[#324c48]/20">
          <TriangleAlert className="h-4 w-4 text-[#324c48]" />
          <AlertTitle className="text-[#324c48]">This page failed to load</AlertTitle>
          <AlertDescription className="text-sm text-gray-600">
            A route-level error occurred. Retry this page or navigate to another route.
          </AlertDescription>
        </Alert>
        <Button
          onClick={this.handleRetry}
          className="mt-3 bg-[#324c48] text-white"
          id="route-error-boundary-btn-retry"
        >
          Retry Page
          <RefreshCw className="ml-2 h-4 w-4" />
        </Button>
      </div>
    );
  }
}

export default function RouteErrorBoundary({ children, routeName }) {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner routeName={routeName} pathname={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
